import { Add as AddIcon } from '@mui/icons-material';
import { Alert, Box, Button, CircularProgress, Paper, Typography } from '@mui/material';
import { JSX, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { parseApiError } from '../../../library/apiErrorHelper';
import { IRoom, RoomFormValues, toRoomRequest } from '../../../library/models/room';
import { CatalogTypeChoice, IServiceCatalog, catalogTypeChoices } from '../../../library/models/serviceCatalog';
import ResourcesIds from '../../../resources/ResourcesIds';
import { useReactMutation, useReactQuery } from '../../../services/queryService';
import { UpdateRoomVariables } from '../../../services/roomsService';
import { services } from '../../../services/services';
import { ConfirmationDialog } from '../../confirmationDialog/confirmationDialog';
import { useSnackbar } from '../../layout/snackbarProvider';
import { RecordCard } from '../../recordCard/RecordCard';
import { cardListStyles } from '../../recordCard/recordCard.styles';
import { RoomFormDialog } from './RoomFormDialog';
import { cardHintStyles, cardStyles, cardTitleStyles, emptyStyles, headerStyles, sectionStyles } from './roomsSettings.styles';

/**
 * The clinic's treatment rooms: how many of each kind, and which services need one.
 *
 * Two therapists who both give every service can still not both run a kineto session at ten
 * if the clinic has one kineto room. A room here is what says so, and the API then refuses a
 * booking -- and offers no slot -- wherever a room's services already fill every room of its
 * kind. Nobody but the administrator sees this tab or the rooms; everybody else just finds
 * the hour gone.
 */
export const RoomsSettings = (): JSX.Element => {
    const { i18n, t: translate } = useTranslation();
    const { showSnackbar } = useSnackbar();

    const { data: rooms, isLoading, refetch } = useReactQuery<IRoom[]>(services.rooms.getRooms());
    const { data: catalog } = useReactQuery<IServiceCatalog>(services.serviceCatalog.getServiceCatalog());

    const createRoom = useReactMutation(services.rooms.createRoomMutation());
    const updateRoom = useReactMutation<IRoom, Error, UpdateRoomVariables>(services.rooms.updateRoomMutation());
    const deleteRoom = useReactMutation<void, Error, number>(services.rooms.deleteRoomMutation());

    const [isFormOpen, setIsFormOpen] = useState(false);
    /** The room being edited, or null while adding one. */
    const [roomUnderEdit, setRoomUnderEdit] = useState<IRoom | null>(null);
    const [roomToDelete, setRoomToDelete] = useState<IRoom | null>(null);

    const choices: CatalogTypeChoice[] = useMemo(
        () => catalogTypeChoices(catalog, i18n.language),
        [catalog, i18n.language]
    );

    const openForm = (room: IRoom | null): void => {
        setRoomUnderEdit(room);
        setIsFormOpen(true);
    };

    /** Rejects on failure, so the dialog can put the API's answer on its fields. */
    const submitForm = async (values: RoomFormValues): Promise<void> => {
        if (roomUnderEdit) {
            await updateRoom.mutateAsync({ id: roomUnderEdit.id, payload: toRoomRequest(values) });
        } else {
            await createRoom.mutateAsync(toRoomRequest(values));
        }

        showSnackbar(translate(roomUnderEdit
            ? ResourcesIds.ROOMS_UPDATED_SUCCESS
            : ResourcesIds.ROOMS_CREATED_SUCCESS), 'success');
        setIsFormOpen(false);
        refetch();
    };

    const confirmDelete = (): void => {
        if (!roomToDelete) {
            return;
        }

        deleteRoom.mutate(roomToDelete.id, {
            onSuccess: () => {
                showSnackbar(translate(ResourcesIds.ROOMS_DELETED_SUCCESS), 'success');
                refetch();
            },
            onError: (error: unknown) => showSnackbar(
                parseApiError(error).message ?? translate(ResourcesIds.ROOMS_DELETE_ERROR), 'error'),
        });

        setRoomToDelete(null);
    };

    /** In price-list order, and only what the price list still names. */
    const servicesOf = (room: IRoom): string => choices
        .filter(choice => room.catalogTypeIds.includes(choice.id))
        .map(choice => choice.name)
        .join(', ') || translate(ResourcesIds.ROOMS_SERVICES_NONE);

    if (isLoading) {
        return (
            <Box sx={sectionStyles}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={sectionStyles}>
            <Paper sx={cardStyles} elevation={0}>
                <Box sx={headerStyles}>
                    <Typography variant="h6" sx={cardTitleStyles}>
                        {translate(ResourcesIds.ROOMS_TITLE)}
                    </Typography>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => openForm(null)}>
                        {translate(ResourcesIds.ROOMS_ADD_ACTION)}
                    </Button>
                </Box>
                <Typography variant="body2" sx={cardHintStyles}>
                    {translate(ResourcesIds.ROOMS_DESCRIPTION)}
                </Typography>

                {rooms === undefined ? (
                    <Alert severity="error">{translate(ResourcesIds.ROOMS_LOAD_ERROR)}</Alert>
                ) : rooms.length === 0 ? (
                    <Typography variant="body2" sx={emptyStyles}>
                        {translate(ResourcesIds.ROOMS_EMPTY)}
                    </Typography>
                ) : (
                    // Cards at every width: a room is three facts and two buttons, which a
                    // table would only stretch.
                    <Box sx={cardListStyles}>
                        {rooms.map(room => (
                            <RecordCard
                                key={room.id}
                                title={room.name}
                                fields={[
                                    { label: translate(ResourcesIds.ROOMS_CAPACITY_LABEL), value: room.capacity },
                                    { label: translate(ResourcesIds.ROOMS_SERVICES_LABEL), value: servicesOf(room) },
                                ]}
                                actions={
                                    <>
                                        <Button size="small" onClick={() => openForm(room)}>
                                            {translate(ResourcesIds.EDIT_TEXT)}
                                        </Button>
                                        <Button size="small" color="error" onClick={() => setRoomToDelete(room)}>
                                            {translate(ResourcesIds.ROOMS_DELETE_ACTION)}
                                        </Button>
                                    </>
                                }
                            />
                        ))}
                    </Box>
                )}
            </Paper>

            <RoomFormDialog
                isOpen={isFormOpen}
                room={roomUnderEdit}
                rooms={rooms ?? []}
                choices={choices}
                onClose={() => setIsFormOpen(false)}
                onSubmit={submitForm}
            />

            <ConfirmationDialog
                isOpen={roomToDelete !== null}
                onClose={() => setRoomToDelete(null)}
                onConfirm={confirmDelete}
                title={translate(ResourcesIds.ROOMS_DELETE_TITLE)}
                description={translate(ResourcesIds.ROOMS_DELETE_DESCRIPTION)}
                confirmMessage={translate(ResourcesIds.ROOMS_DELETE_ACTION)}
                denyMessage={translate(ResourcesIds.CANCEL_TEXT)}
            />
        </Box>
    );
};
